import { OrganizationBoundaryNotice } from 'src/components/organization-context/OrganizationBoundaryNotice';
import { notifyUnhandledError } from 'src/utils/commonErrorUtils';
import { Alert, Box, Button, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { ModuleHeader } from 'src/components/module-header/ModuleHeader';
import {
  DATA_GRID_COLUMN_WIDTH,
  DATA_GRID_COLUMN_WIDTH_3,
  DATA_GRID_COLUMN_WIDTH_5,
} from 'src/constants/Layout';

import { useAuthStore } from 'src/stores/AuthStore/AuthStore';
import { useAlertStore } from 'src/stores/AlertStore/AlertStore';
import { UNHANDLED_ERROR } from 'src/constants/errors';
import { observer } from 'mobx-react-lite';
import TablePaginationActions from 'src/constants/TablePaginationActions';
import { PAGE_SIZE_OPTIONS } from 'src/constants/utils';
import { ROLES } from 'src/constants/Role';
import ErrorBoundary from 'src/components/error-boundary/ErrorBoundry';
import { MODULE_USAGE_DESCRIPTION } from 'src/constants/toolTip';
import { useOrganizationStore } from 'src/stores/OrganizationStore/OrganizationStore';
import { NotAuthorizedCard } from 'src/components/not-authorized-card/NotAuthorizedCard';
import { getAllOrganizationsUserIsMemberOf } from 'src/services/organization';
import {
  OrganizationApiResponseWithPopulatedPlan,
  OrganizationMembershipWithPopulatedOrg,
} from 'src/types/organization';
import { BOT_NAME } from 'src/constants/Bot';
import { ENTRY_CASES, PATHS } from 'src/constants/navigateRoutes';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CustomSection from 'src/components/custom-section';
import { useUserSettingsStore } from 'src/stores/UserSettingsStore/UserSettingsStore';
import {
  getOrganizationBillingTrialDaysRemaining,
  getOrganizationEffectivePlanDisplayName,
  hasActiveOrganizationBillingTrial,
} from 'src/utils/organizationBilling';
import InfoDrawerTrigger from 'src/components/info-drawer-trigger/InfoDrawerTrigger';
import {
  ACTIVE_ORGANIZATION_INFO_MARKDOWN,
  ACTIVE_ORGANIZATION_INFO_TITLE,
  ORGANIZATIONS_MODULE_INFO_MARKDOWN,
  ORGANIZATIONS_MODULE_INFO_TITLE,
  ORGANIZATION_MEMBERSHIPS_INFO_MARKDOWN,
  ORGANIZATION_MEMBERSHIPS_INFO_TITLE,
} from 'src/constants/moduleHelp';

export const allowedRoles: Array<keyof typeof ROLES> = [
  ROLES.ADMIN,
  ROLES.SUPER_ADMIN,
  ROLES.DOMAIN_OWNER,
];
const Organizations = observer(() => {
  const { notify } = useAlertStore();
  const { getUserDetails, fetchUserDetails } = useAuthStore();
  const userDetails = getUserDetails();
  const { organizationDetails, changeActiveOrganization } =
    useOrganizationStore();
  const { resetFilters } = useUserSettingsStore();
  const [orgs, setOrgs] = useState<OrganizationMembershipWithPopulatedOrg[]>(
    [],
  );
  const [loadingForDataGrid, setLoadingForDataGrid] = useState<boolean>(true);
  const [rowCountState, setRowCountState] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 1,
    pageSize: PAGE_SIZE_OPTIONS[0],
  });

  const location = useLocation();
  const navigate = useNavigate();
  const isNoActiveOrganizationPage =
    location.pathname === PATHS.noActiveOrganization;

  const getOrganizationPlanLabel = (
    organization: OrganizationApiResponseWithPopulatedPlan,
  ) => {
    const effectivePlan = getOrganizationEffectivePlanDisplayName(organization);

    if (!hasActiveOrganizationBillingTrial(organization)) {
      return effectivePlan;
    }

    const remainingDays =
      getOrganizationBillingTrialDaysRemaining(organization);

    return `${effectivePlan} free trial (${remainingDays} day${
      remainingDays === 1 ? '' : 's'
    } left)`;
  };

  const startNewOrgOnboardingFlow = () => {
    // We will navigate to a route to start the new org onboarding flow, this will not be done with any popup or some form
    // We need to ask a series of good questions to understand

    navigate(`${PATHS.onBoardRegistration}?entry=${ENTRY_CASES.newOrg}`);
  };

  const joinNewOrgOnboardingFlow = () => {
    // We will navigate to a route to start the  org onboarding flow, this will not be done with any popup or some form
    // We need to ask a series of good questions to understand

    navigate(`${PATHS.onBoardRegistration}?entry=${ENTRY_CASES.joinOrg}`);
  };

  const handleChangeActiveOrg = async (orgId: string) => {
    const changedOrg = await changeActiveOrganization(orgId);

    if (!changedOrg.success) {
      notify(changedOrg.message ?? UNHANDLED_ERROR, 'error');

      return;
    }

    resetFilters();
    await fetchUserDetails();
    window.location.href = PATHS.overview;
  };

  const columnsForActiveOrg: GridColDef<OrganizationApiResponseWithPopulatedPlan>[] =
    [
      {
        field: 'displayName',
        headerName: 'Display Name',
        width: DATA_GRID_COLUMN_WIDTH_5,
        renderCell: ({ row }) => {
          return (
            <Typography
              component="p"
              variant="body1"
              sx={{ maxWidth: DATA_GRID_COLUMN_WIDTH, margin: '16px 0' }}
            >
              {row?.displayName}
            </Typography>
          );
        },
      },
      {
        field: 'planId.displayName',
        headerName: 'Plan Name',
        width: DATA_GRID_COLUMN_WIDTH_5,
        renderCell: ({ row }) => (
          <Typography component="p" variant="body1" sx={{ maxWidth: 450 }}>
            {getOrganizationPlanLabel(row)}
          </Typography>
        ),
      },
      {
        field: 'purchasedSeats',
        headerName: 'Seats Purchased',
        width: DATA_GRID_COLUMN_WIDTH_5,
        renderCell: ({ row }) => (
          <Typography component="p" variant="body1" sx={{ maxWidth: 450 }}>
            {row.purchasedSeats}
          </Typography>
        ),
      },
      {
        field: 'website',
        headerName: 'Website',
        width: DATA_GRID_COLUMN_WIDTH,
        renderCell: ({ row }) => (
          <Typography component="p" variant="body1" sx={{ maxWidth: 450 }}>
            {row.website ?? 'NA'}
          </Typography>
        ),
      },
      {
        field: 'createdAt',
        headerName: 'Org Created At',
        width: DATA_GRID_COLUMN_WIDTH_3,
        renderCell: ({ row }) => {
          return (
            <Typography component="p" variant="body1">
              {new Date(row.createdAt).toLocaleDateString()}
            </Typography>
          );
        },
      },
    ];

  const columns: GridColDef<OrganizationMembershipWithPopulatedOrg>[] = [
    {
      field: 'organizationDetails.displayName',
      headerName: 'Display Name',
      width: DATA_GRID_COLUMN_WIDTH_5,
      renderCell: ({ row }) => {
        return (
          <Typography
            component="p"
            variant="body1"
            sx={{ maxWidth: DATA_GRID_COLUMN_WIDTH, margin: '16px 0' }}
          >
            {row?.organizationDetails.displayName}
          </Typography>
        );
      },
    },
    {
      field: 'markActive',
      headerName: 'Mark Active',
      sortable: false,
      filterable: false,
      width: 170,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
          }}
        >
          <Button
            id="mark-active"
            variant="contained"
            color="primary"
            size="small"
            disabled={
              !userDetails ||
              params.row.organizationDetails._id.toString() ===
                userDetails.organizationId
            }
            onClick={() =>
              handleChangeActiveOrg(
                params.row.organizationDetails._id.toString(),
              )
            }
          >
            Mark as Active
          </Button>
        </Box>
      ),
    },
    {
      field: 'organizationDetails.planId.displayName',
      headerName: 'Plan Name',
      width: DATA_GRID_COLUMN_WIDTH_5,
      renderCell: ({ row }) => (
        <Typography component="p" variant="body1" sx={{ maxWidth: 450 }}>
          {getOrganizationPlanLabel(row.organizationDetails)}
        </Typography>
      ),
    },
    {
      field: 'organizationDetails.purchasedSeats',
      headerName: 'Seats Purchased',
      width: DATA_GRID_COLUMN_WIDTH_5,
      renderCell: ({ row }) => (
        <Typography component="p" variant="body1" sx={{ maxWidth: 450 }}>
          {row.organizationDetails.purchasedSeats}
        </Typography>
      ),
    },
    {
      field: 'organizationDetails.website',
      headerName: 'Website',
      width: DATA_GRID_COLUMN_WIDTH,
      renderCell: ({ row }) => (
        <Typography component="p" variant="body1" sx={{ maxWidth: 450 }}>
          {row.organizationDetails.website ?? 'NA'}
        </Typography>
      ),
    },
    {
      field: 'role',
      headerName: 'Your Role',
      width: DATA_GRID_COLUMN_WIDTH_5,
      renderCell: ({ row }) => (
        <Typography component="p" variant="body1" sx={{ maxWidth: 450 }}>
          {row.role}
        </Typography>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Your Active Status',
      width: DATA_GRID_COLUMN_WIDTH_5,
      renderCell: ({ row }) => (
        <Typography component="p" variant="body1" sx={{ maxWidth: 450 }}>
          {row.isActive ? 'Active' : 'Inactive'}
        </Typography>
      ),
    },
    {
      field: 'organizationDetails.createdAt',
      headerName: 'Org Created At',
      width: DATA_GRID_COLUMN_WIDTH_3,
      renderCell: ({ row }) => {
        return (
          <Typography component="p" variant="body1">
            {new Date(row.organizationDetails.createdAt).toLocaleDateString()}
          </Typography>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Joined At',
      width: DATA_GRID_COLUMN_WIDTH_3,
      renderCell: ({ row }) => {
        return (
          <Typography component="p" variant="body1">
            {new Date(row.createdAt).toLocaleDateString()}
          </Typography>
        );
      },
    },
  ];

  const fetchAndSetOrganizations = useCallback(async () => {
    if (!userDetails) {
      setLoadingForDataGrid(false);

      return;
    }

    try {
      const { success: getOrgMembershipsSuccess, content: orgsMembershipData } =
        await getAllOrganizationsUserIsMemberOf(
          paginationModel.page,
          paginationModel.pageSize,
        );

      if (!getOrgMembershipsSuccess) {
        notify(UNHANDLED_ERROR, 'error');
        setLoadingForDataGrid(false);
        setOrgs([]);

        return;
      }

      if (orgsMembershipData?.data) {
        const updatedContent = orgsMembershipData.data.map((membership) => ({
          id: membership.organizationDetails._id.toString(),
          ...membership,
        }));

        setRowCountState(orgsMembershipData.totalCount);
        setOrgs(updatedContent);
      }
    } catch (error) {
      await notifyUnhandledError(error, notify);
      setOrgs([]);
    } finally {
      setLoadingForDataGrid(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, notify, userDetails]);

  useEffect(() => {
    if (!userDetails) return;

    fetchAndSetOrganizations();
  }, [fetchAndSetOrganizations]);

  if (!userDetails) {
    return <ErrorBoundary message={UNHANDLED_ERROR} />;
  }

  return (
    <Box component="main">
      <ModuleHeader
        title="Organizations (/teams)"
        toolTipProps={{
          clickId: 'organizations',
          toolTipDescription: MODULE_USAGE_DESCRIPTION.ORGANIZATIONS,
        }}
        infoDrawerProps={{
          title: ORGANIZATIONS_MODULE_INFO_TITLE,
          markdownContent: ORGANIZATIONS_MODULE_INFO_MARKDOWN,
          ariaLabel: ORGANIZATIONS_MODULE_INFO_TITLE,
          iconButtonId: 'organizations_module_info',
        }}
      />
      <OrganizationBoundaryNotice />
      <Alert severity="info">
        At any given time, only 1 organization can be active. Each organization
        has isolated data and memories from other organizations. {BOT_NAME} will
        take the data and context of the active org when replying to your
        queries in Google Chat.
      </Alert>
      <Alert severity="info" sx={{ mt: 2 }}>
        You can edit the organization settings in the{' '}
        <Link id="go_to_settings_info_callout_orgs_module" to={PATHS.settings}>
          settings module
        </Link>{' '}
        settings module after marking the org as active here.
      </Alert>
      <CustomSection
        sx={{ mt: 2 }}
        title="Currently Active Organization"
        leftSideCta={
          <InfoDrawerTrigger
            title={ACTIVE_ORGANIZATION_INFO_TITLE}
            markdownContent={ACTIVE_ORGANIZATION_INFO_MARKDOWN}
            ariaLabel={ACTIVE_ORGANIZATION_INFO_TITLE}
            iconButtonId="active_organization_info"
          />
        }
      />
      <Box>
        <DataGrid
          getRowId={(row) => row._id}
          getRowHeight={() => 'auto'}
          autoHeight
          rows={organizationDetails ? [organizationDetails] : []}
          columns={columnsForActiveOrg.map((column) => ({
            ...column,
            sortable: false,
          }))}
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooterPagination
        />
      </Box>
      <CustomSection
        sx={{ mt: 2 }}
        title="All Organizations"
        leftSideCta={
          <InfoDrawerTrigger
            title={ORGANIZATION_MEMBERSHIPS_INFO_TITLE}
            markdownContent={ORGANIZATION_MEMBERSHIPS_INFO_MARKDOWN}
            ariaLabel={ORGANIZATION_MEMBERSHIPS_INFO_TITLE}
            iconButtonId="organization_memberships_info"
          />
        }
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          marginBottom: '16px',
          mt: 2,
        }}
      >
        <Button
          id="join-organization"
          variant="outlined"
          size="small"
          onClick={() => joinNewOrgOnboardingFlow()}
          sx={{ mr: 2 }}
        >
          + Join Org
        </Button>
        <Button
          id="create-organization"
          variant="contained"
          color="primary"
          size="small"
          onClick={() => startNewOrgOnboardingFlow()}
        >
          + Create Org
        </Button>
      </Box>
      <Box>
        {!isNoActiveOrganizationPage &&
          !allowedRoles.includes(userDetails.role) && <NotAuthorizedCard />}
        <DataGrid
          getRowHeight={() => 'auto'}
          autoHeight
          paginationMode="server"
          rows={orgs}
          columns={columns.map((column) => ({
            ...column,
            sortable: false,
          }))}
          disableColumnMenu
          disableRowSelectionOnClick
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={loadingForDataGrid}
          pagination
          initialState={{
            pagination: {
              paginationModel: {
                page: paginationModel.page - 1,
                pageSize: paginationModel.pageSize,
              },
            },
          }}
          slotProps={{
            pagination: {
              ActionsComponent: TablePaginationActions,
            },
          }}
          rowCount={rowCountState}
          onPaginationModelChange={(newPaginationModel) => {
            setPaginationModel((oldPaginationModel) => ({
              ...oldPaginationModel,
              page: newPaginationModel.page + 1,
              pageSize: newPaginationModel.pageSize,
            }));
          }}
        />
      </Box>
    </Box>
  );
});

export default Organizations;
